import ModalBlank from "@shared/components/modals/ModalBlank";
import theme from "@shared/utils/themeColors";
import React, { useState } from "react";
import { ConfirmationModal } from "../../../shared/components/ConfirmationModal";
import EditMenu from "../../../shared/components/EditMenu";
import { SubTask } from "../types/task.types";
import { SubTaskForm } from "./SubTaskForm";

interface SubTaskItemProps {
  taskId: string;
  subtask: SubTask;
  onToggle: (taskId: string, subtaskId: string) => Promise<void>;
  onUpdate: (taskId: string, subtaskId: string, title: string) => Promise<void>;
  onDelete: (taskId: string, subtaskId: string) => Promise<void>;
  onReorder: (
    taskId: string,
    action: ["move-up" | "move-down", string]
  ) => Promise<void>;
  isCompleted?: boolean;
  totalSubtasks: number;
  position: number;
}

export const SubTaskItem: React.FC<SubTaskItemProps> = ({
  taskId,
  subtask,
  onToggle,
  onUpdate,
  onDelete,
  onReorder,
  isCompleted,
  totalSubtasks,
  position,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(taskId, subtask.id);
    } catch (error) {
      console.error("Error deleting subtask:", error);
      // toast.error('Failed to delete subtask');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleMoveUp = async () => {
    await onReorder(taskId, ["move-up", subtask.id]);
  };

  const handleMoveDown = async () => {
    await onReorder(taskId, ["move-down", subtask.id]);
  };

  const showMoveButtons = totalSubtasks > 1;
  const canMoveUp = position > 0;
  const canMoveDown = position < totalSubtasks - 1;

  return (
    <div className="flex items-center justify-between group">
      <label className="flex items-center flex-1">
        <input
          type="checkbox"
          className="form-checkbox w-5 h-5 rounded-full peer cursor-pointer"
          checked={subtask.completed}
          onChange={() => onToggle(taskId, subtask.id)}
        />
        <span
          className={`text-sm text-gray-800 dark:text-gray-100 peer-checked:line-through ml-3 ${
            isCompleted ? "opacity-60" : ""
          }`}
        >
          {subtask.title}
        </span>
      </label>

      <div className="transition-opacity">
        <EditMenu align="right" className="relative inline-flex">
          <li>
            <button
              className="font-medium text-sm text-gray-600 hover:text-gray-700 flex py-1 px-3 items-center"
              onClick={() => setShowEditModal(true)}
            >
              <svg
                className="w-4 h-4 fill-current shrink-0"
                viewBox="0 0 16 16"
              >
                <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L9.4 4 11 2.4 13.6 5 12 6.6z" />
              </svg>
              <span className="ml-2">Edit</span>
            </button>
          </li>
          {showMoveButtons && canMoveUp && (
            <li>
              <button
                className="font-medium text-sm text-gray-600 hover:text-gray-700 flex py-1 px-3 items-center"
                onClick={handleMoveUp}
              >
                <svg
                  className="w-4 h-4 fill-current shrink-0"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 4.414l3.293 3.293a1 1 0 01-1.414 1.414L8.707 7.95V12a1 1 0 11-2 0V7.95L5.536 9.121a1 1 0 11-1.414-1.414L7.414 4.414a1 1 0 011.172 0z" />
                </svg>
                <span className="ml-2">Move Up</span>
              </button>
            </li>
          )}
          {showMoveButtons && canMoveDown && (
            <li>
              <button
                className="font-medium text-sm text-gray-600 hover:text-gray-700 flex py-1 px-3 items-center"
                onClick={handleMoveDown}
              >
                <svg
                  className="w-4 h-4 fill-current shrink-0"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 11.586l-3.293-3.293a1 1 0 011.414-1.414l1.172 1.172V4a1 1 0 112 0v4.05l1.172-1.171a1 1 0 111.414 1.414L8.586 11.586a1 1 0 01-1.172 0z" />
                </svg>
                <span className="ml-2">Move Down</span>
              </button>
            </li>
          )}
          <li>
            <button
              className={`font-medium text-sm ${theme.text.error} hover:text-red-600 flex py-1 px-3 items-center`}
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <svg
                className={`w-4 h-4 fill-current ${theme.text.error} shrink-0`}
                viewBox="0 0 16 16"
              >
                <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
              </svg>
              <span className="ml-2">Delete</span>
            </button>
          </li>
        </EditMenu>
      </div>

      {/* Edit Modal */}
      <ModalBlank
        id="edit-subtask-modal"
        modalOpen={showEditModal}
        setModalOpen={setShowEditModal}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
              Edit Subtask
            </h2>
            <button
              className="text-slate-400 hover:text-slate-500"
              onClick={() => setShowEditModal(false)}
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4 fill-current">
                <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.414L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
              </svg>
            </button>
          </div>
          <SubTaskForm
            initialTitle={subtask.title}
            submitText="Save Changes"
            onSubmit={async (title) => {
              await onUpdate(taskId, subtask.id, title);
              setShowEditModal(false);
            }}
            onCancel={() => setShowEditModal(false)}
          />
        </div>
      </ModalBlank>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Delete Subtask"
        message="Are you sure you want to delete this subtask? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
